import { z } from 'zod';
import { checkFilter } from '#src/rsql/filter.js';
import { DEFAULT_PAGE_SIZE, limitsWith } from '#src/rsql/limits.js';
import { parseRsql } from '#src/rsql/parser.js';
const SORT_ITEM = /^(-?)([A-Za-z][A-Za-z0-9_]*(?:\.[A-Za-z][A-Za-z0-9_]*)*)$/;
/** `-createdAt,recipientEmail`: `-` é decrescente. */
function parseSort(text, policy, maxFields) {
    const items = text.split(',');
    if (items.length > maxFields) {
        throw new Error(`no máximo ${maxFields} campos`);
    }
    const seen = new Set();
    return items.map((item) => {
        const match = SORT_ITEM.exec(item);
        const name = match?.[2];
        if (name === undefined) {
            throw new Error(`"${item}" não é um campo; use campo ou -campo`);
        }
        const field = policy.fields[name];
        if (!Object.hasOwn(policy.fields, name) || field?.sortable !== true) {
            const sortable = Object.entries(policy.fields)
                .filter(([, value]) => value.sortable === true)
                .map(([key]) => key);
            throw new Error(`"${name}" não é ordenável; use ${sortable.join(', ')}`);
        }
        if (seen.has(name)) {
            throw new Error(`"${name}" repetido`);
        }
        seen.add(name);
        return {
            field: name,
            direction: match?.[1] === '-' ? 'desc' : 'asc',
        };
    });
}
function parseFields(text, policy) {
    const names = text.split(',');
    const unknown = names.filter((name) => !policy.selectable.includes(name));
    if (unknown.length > 0) {
        throw new Error(`${unknown.join(', ')} não pode ser pedido; use ${policy.selectable.join(', ')}`);
    }
    return [...new Set(names)];
}
/** Converte um erro de parse em issue do zod, no parâmetro certo. */
function attempt(ctx, path, parse) {
    try {
        return parse();
    }
    catch (error) {
        ctx.addIssue({
            code: 'custom',
            path: [path],
            message: error instanceof Error ? error.message : String(error),
        });
        return undefined;
    }
}
/**
 * Schema da query string de uma listagem: `page`, `pageSize`, `filter`
 * (RSQL), `sort` e `fields`. Parâmetros desconhecidos são recusados.
 */
export function listQuerySchema(policy) {
    const limits = limitsWith(policy.limits);
    return z
        .strictObject({
        page: z.coerce.number().int().min(1).default(1),
        pageSize: z.coerce
            .number()
            .int()
            .min(1)
            .max(limits.maxPageSize)
            .default(Math.min(DEFAULT_PAGE_SIZE, limits.maxPageSize)),
        filter: z.string().max(limits.maxFilterLength).optional(),
        sort: z.string().max(500).optional(),
        fields: z.string().max(500).optional(),
    })
        .transform((query, ctx) => {
        const offset = (query.page - 1) * query.pageSize;
        if (offset > limits.maxOffset) {
            ctx.addIssue({
                code: 'custom',
                path: ['page'],
                message: `páginas além do item ${limits.maxOffset} não são servidas; refine o filtro`,
            });
        }
        const filter = query.filter === undefined
            ? null
            : attempt(ctx, 'filter', () => checkFilter(parseRsql(query.filter ?? '', limits), policy));
        const sort = query.sort === undefined
            ? [...policy.defaultSort]
            : attempt(ctx, 'sort', () => parseSort(query.sort ?? '', policy, limits.maxSortFields));
        const fields = query.fields === undefined
            ? null
            : attempt(ctx, 'fields', () => parseFields(query.fields ?? '', policy));
        if (filter === undefined || sort === undefined || fields === undefined) {
            return z.NEVER;
        }
        return {
            page: query.page,
            pageSize: query.pageSize,
            offset,
            filter,
            sort,
            fields,
        };
    });
}
/** Mantém só os campos pedidos em `fields`; `null` devolve tudo. */
export function selectFields(item, fields) {
    if (fields === null) {
        return item;
    }
    const entries = Object.entries(item).filter(([key]) => fields.includes(key));
    return Object.fromEntries(entries);
}
//# sourceMappingURL=query.js.map